// Create a new directory and file for the extracted component
// File: /home/mad/projects/Englobe-admin/resources/js/components/SidePanelWithFloatingButton.tsx

import { Button } from '@app/components/ui/button';
import { Brush } from 'lucide-react';
import { useEffect, useState } from 'react';
import {useTranslation} from 'react-i18next';

function convertCssVariableToHex(computedColor: string): string {
    const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (rgbMatch) {
        const [, r, g, b] = rgbMatch.map(Number);
        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    }

    const oklchMatch = computedColor.match(/^oklch\(\s*([\d.]+)\s*[\s,]\s*([\d.]+)\s*[\s,]\s*([\d.]+)\s*\)$/);
    if (oklchMatch) {
        const [, l, c, h] = oklchMatch.map(Number);
        const [r, g, b] = oklchToRgb(l, c, h);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    const hslMatch = computedColor.match(/^hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)$/);
    if (hslMatch) {
        const [, h, s, l] = hslMatch.map(Number);
        const a = s * Math.min(l / 100, 1 - l / 100);
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return Math.round(255 * color).toString(16).padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    }

    return computedColor;
}

export function oklchToRgb(l: number, c: number, h: number, a?: number) {
    const oklch2oklab = ([l, c, h]: [number, number, number]) => [
        l,
        isNaN(h) ? 0 : c * Math.cos(h * Math.PI / 180),
        isNaN(h) ? 0 : c * Math.sin(h * Math.PI / 180)
    ];

    const multiplyMatrices = (A: number[], B: number[]) => {
        return [
            A[0] * B[0] + A[1] * B[1] + A[2] * B[2],
            A[3] * B[0] + A[4] * B[1] + A[5] * B[2],
            A[6] * B[0] + A[7] * B[1] + A[8] * B[2]
        ];
    };

    const oklab2xyz = (lab: number[]) => {
        const LMSg = multiplyMatrices([
            1, 0.3963377773761749, 0.2158037573099136,
            1, -0.1055613458156586, -0.0638541728258133,
            1, -0.0894841775298119, -1.2914855480194092
        ], lab);
        const LMS = LMSg.map(val => val ** 3);
        return multiplyMatrices([
            1.2268798758459243, -0.5578149944602171, 0.2813910456659647,
            -0.0405757452148008, 1.1122868032803170, -0.0717110580655164,
            -0.0763729366746601, -0.4214933324022432, 1.5869240198367816
        ], LMS);
    };

    const xyz2rgbLinear = (xyz: number[]) => {
        return multiplyMatrices([
            3.2409699419045226, -1.537383177570094, -0.4986107602930034,
            -0.9692436362808796, 1.8759675015077202, 0.04155505740717559,
            0.05563007969699366, -0.20397695888897652, 1.0569715142428786
        ], xyz);
    };

    const srgbLinear2rgb = (rgb: number[]) => rgb.map(c =>
        Math.abs(c) > 0.0031308 ?
            (c < 0 ? -1 : 1) * (1.055 * (Math.abs(c) ** (1 / 2.4)) - 0.055) :
            12.92 * c
    );

    const oklch2rgb = (lch: [number, number, number]) =>
        srgbLinear2rgb(xyz2rgbLinear(oklab2xyz(oklch2oklab(lch))));

    const rgb = oklch2rgb([l, c, h]).map((val) => Math.max(0, Math.min(255, Math.round(val * 255))));
    return a === undefined ? rgb : [...rgb, a];
}

const SidePanel = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const {t} = useTranslation();
    const [colors, setColors] = useState<Record<string, string>>({});

    useEffect(() => {
        const tmp: Record<string, HTMLDivElement> = {};
        const list = [
            '--background',
            '--foreground',
            '--card',
            '--card-foreground',
            '--popover',
            '--popover-foreground',
            '--primary',
            '--primary-foreground',
            '--secondary',
            '--secondary-foreground',
            '--muted',
            '--muted-foreground',
            '--accent',
            '--accent-foreground',
            '--destructive',
            '--destructive-foreground',
            '--border',
            '--input',
            '--ring',
            '--chart-1',
            '--chart-2',
            '--chart-3',
            '--chart-4',
            '--chart-5',
            '--sidebar',
            '--sidebar-foreground',
            '--sidebar-primary',
            '--sidebar-primary-foreground',
            '--sidebar-accent',
            '--sidebar-accent-foreground',
            '--sidebar-border',
            '--sidebar-ring',
        ];

        for (const c of list) {
            const element = document.createElement('div');
            element.style.backgroundColor = `var(${c})`;
            document.body.appendChild(element);
            tmp[c] = element;
        }

        setTimeout(() => {
            setColors(Object.fromEntries(
                list.map(c => {
                    const computedColor = getComputedStyle(tmp[c]).backgroundColor;
                    document.body.removeChild(tmp[c]);
                    return [c, convertCssVariableToHex(computedColor)]
                })
            ));
        }, 1000)
    }, [])

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                right: isOpen ? 0 : '-300px',
                width: '300px',
                height: '100vh',
                transition: 'right 0.3s ease-in-out',
                zIndex: 1000,
                display: "flex",
                flexDirection: "column"
            }}
            className='bg-sidebar border-l border-sidebar-border'
        >
            <Button className='m-2' size={'sm'} onClick={onClose}>{t('common.buttons.close')}</Button>
            <Button className='m-2' size={'sm'}
                onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(colors, null, 2));
                }}
            >
                {t('common.theme-editor.copy-colors')}
            </Button>

            <div className="overflow-y-auto h-full p-4">
                {Object.entries(colors).map(([cssVariable, value]) => (
                    <div key={cssVariable} className="mb-4">
                        <label htmlFor={`colorPicker-${cssVariable}`} className="block text-sm font-medium">
                            {cssVariable}:
                        </label>
                        <input
                            id={`colorPicker-${cssVariable}`}
                            type="color"
                            defaultValue={value}
                            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
                            onChange={(e) => {
                                document.documentElement.style.setProperty(cssVariable, e.target.value);
                            }}
                        />
                    </div>
                ))}
                <div className="mb-4">
                    <label htmlFor={`sli`} className="block text-sm font-medium">
                        radius:
                    </label>
                    <input
                        id={`sli`}
                        type="range"
                        min={0}
                        max={1}
                        step={0.001}
                        className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
                        onChange={(e) => {
                            document.documentElement.style.setProperty("--radius", e.target.value + "rem");
                        }}
                    />
                </div>
            </div>
        </div>
    )
};

const FloatingButton = ({ onClick }: { onClick: () => void }) => (
    <Button
        size="icon"
        onClick={onClick}
        className="fixed bottom-5 right-5 w-8 h-8 p-1 z-50"
    >
        <Brush size={16} />
    </Button>
);

const ThemeEditor = () => {
    const [isSidePanelOpen, setSidePanelOpen] = useState(false);
    return (
        <>
            <SidePanel isOpen={isSidePanelOpen} onClose={() => setSidePanelOpen(false)} />
            {!isSidePanelOpen && <FloatingButton onClick={() => setSidePanelOpen(true)} />}
        </>
    );
};

export default ThemeEditor;
